// ===============================================================================
// src/lib/pageTitleCore.js - pure route -> page title derivation.
//
// The phone top bar (Layout.jsx) and document.title both show where you are.
// The labels come from the SAME nav config the sidebar renders (NAV_PATHS +
// NAV_META in Layout), passed in by the caller, so a renamed nav item renames
// its title too. This file only adds the fallbacks for routes that are not a
// nav item (detail pages, sub-pages, unknown paths). No React here so it can
// be unit tested under node --test.
// ===============================================================================

// Routes that live under a nav path but are a single record, not the list.
// Checked before the nav match: "/patients/123" is one Patient, not Patients.
// Deliberately generic ("Patient", never a name) - a view that wants a more
// specific heading opts in with usePhoneTitle() from lib/pageTitle.js.
const DETAIL_TITLES = [
  { prefix: "/patients/", title: "Patient" },
];

// Routes that are not in any role's nav list but are reachable by URL or by
// an off-list sidebar entry (super admin, onboarding).
const EXTRA_TITLES = [
  { prefix: "/admin", title: "Administrator" },
  { prefix: "/onboarding", title: "Setup" },
];

export const APP_NAME = "PracticeOS";

const matches = (pathname, p) => pathname === p || pathname.startsWith(p + "/");

// "/my-training/x" -> "My Training". Last-resort fallback so an unmapped
// route still says something better than the product name.
function titleizeFirstSegment(pathname) {
  const seg = String(pathname || "").split("/").filter(Boolean)[0];
  if (!seg) return null;
  return seg.split(/[-_]+/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") || null;
}

/**
 * derivePageTitle(pathname, navPaths, labelFor) -> string
 *   pathname  location.pathname
 *   navPaths  { navId: "/path" } (Layout's NAV_PATHS)
 *   labelFor  (navId) => label or undefined (Layout's NAV_META lookups)
 * Longest nav path wins, so "/care-management/copilot" beats "/care-management"
 * and every other "/care-management/<section>" falls back to Care Management.
 */
export function derivePageTitle(pathname, navPaths, labelFor) {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  for (const d of DETAIL_TITLES) {
    if (path.startsWith(d.prefix) && path.length > d.prefix.length) return d.title;
  }
  const entries = Object.entries(navPaths || {}).sort((a, b) => b[1].length - a[1].length);
  for (const [id, p] of entries) {
    if (!matches(path, p)) continue;
    const label = labelFor ? labelFor(id) : null;
    if (label) return label;
  }
  for (const e of EXTRA_TITLES) {
    if (matches(path, e.prefix)) return e.title;
  }
  return titleizeFirstSegment(path) || APP_NAME;
}

// "<Page> - PracticeOS", or just the product name when the page IS the product.
export function documentTitleFor(pageTitle) {
  return pageTitle && pageTitle !== APP_NAME ? pageTitle + " - " + APP_NAME : APP_NAME;
}
